/**
 * /dashboard/me/surveys — respondent list of surveys assigned to the caller.
 *
 * Backend contract (GET /api/v1/me/surveys, any authed user):
 *   - Returns surveys that are status=active AND the caller has not
 *     yet responded to. The backend's ListAvailableSurveysForUser
 *     enforces both conditions in SQL.
 *
 * UX:
 *   - Cards-as-rows, click → /dashboard/me/surveys/$id (take-survey).
 *   - Empty state when nothing is assigned.
 *   - Loading skeleton; error alert on fetch failure.
 *
 * G16 — replaces the earlier ComingSoon stub.
 */

import { createFileRoute, Link, Outlet, useParams, useRouterState } from "@tanstack/react-router";
import { ArrowRight, MessagesSquare, ShieldCheck } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useMyAvailableSurveys } from "@/hooks/use-surveys";
import { PageHeader } from "@/components/shared/page-header";
import {
  surveyTypeLabel,
  fmtDateTime,
} from "@/components/surveys/format";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/me/surveys")({
  component: MySurveysRoute,
});

function MySurveysRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const routerState = useRouterState();
  // When a child route (e.g. /me/surveys/{id}) is active, delegate
  // rendering to it via <Outlet /> so the take-survey form renders
  // without being overlaid by the list.
  const q = useMyAvailableSurveys();

  if (/\/me\/surveys\/\d+/.test(routerState.location.pathname)) {
    return <Outlet />;
  }

  const items = q.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="My Surveys"
        description="Pulse, eNPS and other surveys assigned to you. Each one takes a few minutes."
      />

      {q.isLoading && <ListSkeleton />}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load surveys</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data && items.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center text-sm text-muted-foreground">
            <MessagesSquare aria-hidden className="h-8 w-8 text-muted-foreground/60" />
            <p className="font-medium text-gray-700">
              You&apos;re all caught up.
            </p>
            <p>No surveys waiting for your response right now.</p>
          </CardContent>
        </Card>
      )}

      {q.data && items.length > 0 && (
        <div className="space-y-3">
          {items.map((s) => (
            <Link
              key={s.id}
              to="/app/$tenantSlug/me/surveys/$id"
              params={{tenantSlug, id: String(s.id)}}
              className="group block"
              data-testid={`my-survey-row-${s.id}`}
            >
              <Card className="transition-colors group-hover:border-primary/40">
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="font-medium text-gray-900">{s.title}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline">
                          {surveyTypeLabel(s.survey_type)}
                        </Badge>
                        {s.is_anonymous && (
                          <span className="inline-flex items-center gap-1 text-primary">
                            <ShieldCheck aria-hidden className="h-3.5 w-3.5" />
                            Anonymous
                          </span>
                        )}
                        <span>· Updated {fmtDateTime(s.updated_at)}</span>
                      </div>
                      {s.description && (
                        <p className="line-clamp-2 text-sm text-muted-foreground">
                          {s.description}
                        </p>
                      )}
                    </div>
                    <ArrowRight
                      aria-hidden
                      className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                    />
                  </div>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

const SKELETON_ROW_KEYS = ["sk-a", "sk-b", "sk-c"] as const;

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {SKELETON_ROW_KEYS.map((k) => (
        <Card key={k}>
          <CardHeader>
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-2 h-4 w-1/2" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}
